import React from 'react';
import { useNavigate } from 'react-router-dom';
import ClientLayout from '../../components/layout/ClientLayout';
import { useSubscription } from '../../hooks/useSubscription';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2, CreditCard, Check, Sparkles } from 'lucide-react';

const MySubscriptionPage = () => {
  const navigate = useNavigate();
  const { tier, subscription, loading, isFree } = useSubscription();

  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ClientLayout>
    );
  }

  const tierName = subscription?.tier_name || tier?.name || 'Free';
  const renewal = subscription?.current_period_end;
  const renewalStr = renewal ? new Date(renewal).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }) : null;
  const isDerived = subscription?.derived === true;

  return (
    <ClientLayout>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="my-subscription-page">
        <div className="flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">Mi suscripción</h1>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-xl font-bold text-foreground">{tierName}</h2>
                {subscription?.since && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Activo desde {new Date(subscription.since).toLocaleDateString('es-ES')}
                  </p>
                )}
              </div>
              <Badge variant={isFree() ? 'secondary' : 'default'} data-testid="my-sub-badge">
                {isFree() ? 'Plan gratuito' : 'Plan activo'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isDerived && (
              <div className="rounded-lg bg-primary/10 border border-primary/30 p-3 text-sm text-foreground">
                Eres miembro de un plan Familia. El titular gestiona el pago.
              </div>
            )}

            {renewalStr && (
              <div className="text-sm text-muted-foreground">
                Próxima renovación: <span className="text-foreground font-medium">{renewalStr}</span>
              </div>
            )}

            {tier?.limits && (
              <div>
                <p className="text-sm font-semibold text-foreground mb-2">Tu plan incluye:</p>
                <ul className="space-y-1 text-sm">
                  {Object.entries(tier.limits).map(([k, v]) => {
                    if (!v || v === false) return null;
                    const labels = {
                      max_sessions_logged: v === -1 ? 'Sesiones ilimitadas' : `Hasta ${v} sesiones`,
                      can_design_workout: 'Diseña tu Entreno',
                      can_chat_write: 'Chat completo (escribir mensajes)',
                      stats_advanced: 'Estadísticas avanzadas',
                      family_seats: v > 0 ? `Hasta ${v} perfiles familiares` : null,
                    };
                    const text = labels[k];
                    if (!text) return null;
                    return (
                      <li key={k} className="flex items-center gap-2 text-foreground/90">
                        <Check className="h-4 w-4 text-emerald-400" />
                        {text}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="pt-4 border-t border-border flex flex-wrap gap-2">
              {isFree() ? (
                <Button onClick={() => navigate('/pricing')} data-testid="my-sub-upgrade-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Suscribirme a Pro
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => navigate('/pricing')} data-testid="my-sub-change-btn">
                    Cambiar de plan
                  </Button>
                  {subscription?.tier_key?.startsWith('client_family') && !isDerived && (
                    <Button variant="outline" onClick={() => navigate('/portal/familia')} data-testid="my-sub-family-btn">
                      Gestionar familia
                    </Button>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <p className="text-xs text-muted-foreground text-center">
          Para cancelar tu suscripción, contacta con soporte en{' '}
          <a href="mailto:hola@guardiansfit.com" className="text-primary underline">hola@guardiansfit.com</a>.
          Mantendrás acceso hasta el final del periodo pagado.
        </p>
      </div>
    </ClientLayout>
  );
};

export default MySubscriptionPage;
